import { Box, Stack, Alert } from "@mui/material";
import { useEffect, useState, useCallback, useRef } from "react";
import { useIsMobile } from "../../../hooks/isMobile/isMobile";
import { Rules } from "../types/rules";
import useUsers from "../../../hooks/useUsers/useUsers";
import { useAvatarCache } from "../../../contexts/AvatarCacheContext";
import GameResults from "./GameResults";
import ScoreboardTable from "./ScoreboardTable";
import ScoreboardHeader from "./ScoreboardHeader";
import { isGameStarted } from "../utils/game";
import {
  Game,
  SelectedGame,
  PickedGame,
  GetAllPicksForWeekResponse,
} from "../types/game";
import { PollsResponse } from "../types/poll";
import { RecordsResponse } from "../types/records";
import { useCfbd } from "../hooks/useCfbd/useCfbd";
import { useLeague } from "../../../hooks/useLeague/useLeague";
import { useScores } from "../../../hooks/useScores/useScores";
import { PlayerScore, AvailableYearsAndWeeksResponse } from "../types/score";

interface ScoreboardProps {
  leagueId?: string;
  rules?: Rules;
  userEmail?: string;
}

type ViewMode = "year" | "week";

const Scoreboard = ({ leagueId, rules, userEmail }: ScoreboardProps) => {
  const isMobile = useIsMobile();
  const { getAvailableYearsAndWeeks, getYearScores, getWeekScores } =
    useScores();
  const { preloadUsers, getCachedUsers } = useUsers();
  const { preloadAvatars } = useAvatarCache();
  const { getCurrentWeek, getAvailableWeeks, getPollsAndRecords } = useCfbd();
  const { getSelectedGames, getAllPicksForWeek } = useLeague();
  // Helper function to get display name with email fallback
  const getDisplayName = (email: string): string => {
    const cachedUsers = getCachedUsers([email]);
    const user = cachedUsers.find((u) => u.email === email);
    return user?.displayName || email;
  };
  const [viewMode, setViewMode] = useState<ViewMode>("week");
  const [error, setError] = useState<string | null>(null);
  const [dataAvailable, setDataAvailable] = useState(false);

  // Simple, component-specific loading states
  const [loadingHeader, setLoadingHeader] = useState(true);
  const [loadingScores, setLoadingScores] = useState(true);
  const [loadingGames, setLoadingGames] = useState(true);

  // Ref to track if we're already fetching to prevent multiple simultaneous calls
  const fetchingRef = useRef(false);
  // Ref to store the current AbortController for cancelling fetches
  const abortControllerRef = useRef<AbortController | null>(null);
  // Ref to track the current week being fetched to prevent stale data
  const currentFetchRef = useRef<{ year: number; week: number } | null>(null);

  // Year view aggregate standings
  const [yearScores, setYearScores] = useState<PlayerScore[]>([]);

  // Week view data & context
  const [weekScores, setWeekScores] = useState<PlayerScore[]>([]);
  const [selectedGames, setSelectedGames] = useState<SelectedGame[]>([]);
  const [gameOfTheWeek, setGameOfTheWeek] = useState<number | null>(null);
  const [userPicks, setUserPicks] = useState<GetAllPicksForWeekResponse | null>(
    null
  );
  const [weekComplete, setWeekComplete] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [selectedYear, setSelectedYear] = useState<number>(0);
  const [selectedWeek, setSelectedWeek] = useState<number>(0);
  // Calendar-backed week options per year
  const [calendarWeeksByYear, setCalendarWeeksByYear] = useState<
    Record<number, number[]>
  >({});
  const currentCalendarRef = useRef<{ year?: number; week?: number }>({});
  // Response-provided context when dropdowns not yet populated
  const [winningTeams, setWinningTeams] = useState<{
    [gameId: string]: number;
  }>({});
  const [weekReady, setWeekReady] = useState<boolean>(false);

  // Available scores (year->weeks) for dropdowns
  const [availableYearsAndWeeks, setAvailableYearsAndWeeks] = useState<
    AvailableYearsAndWeeksResponse["availableScores"]
  >({});

  // Polls and records data
  const [pollsData, setPollsData] = useState<PollsResponse | null>(null);
  const [recordsData, setRecordsData] = useState<RecordsResponse | null>(null);

  const fetchCalendarForYear = useCallback(
    async (year: number) => {
      try {
        const weeksResult = await getAvailableWeeks(year);
        if (weeksResult instanceof Error) return;
        setCalendarWeeksByYear((prev) => ({ ...prev, [year]: weeksResult }));
        return weeksResult;
      } catch {
        return undefined;
      }
    },
    [getAvailableWeeks]
  );

  const fetchCalendarCurrent = useCallback(async () => {
    try {
      const currentResult = await getCurrentWeek();
      if (currentResult instanceof Error) return;

      currentCalendarRef.current = {
        year: currentResult.year,
        week: currentResult.week,
      };
      // If no selection yet, set sensible defaults from calendar
      if (!selectedYear && typeof currentResult.year === "number")
        setSelectedYear(currentResult.year);
      if (
        !selectedWeek &&
        typeof currentResult.week === "number" &&
        currentResult.week > 0
      )
        setSelectedWeek(currentResult.week);
      // Preload that year's weeks
      if (typeof currentResult.year === "number")
        fetchCalendarForYear(currentResult.year);
    } catch {}
  }, [getCurrentWeek, fetchCalendarForYear, selectedYear, selectedWeek]);

  // Determine game result for a specific user
  const getGameResult = (
    game: Game,
    userPick?: PickedGame
  ): "correct" | "incorrect" | "push" | "pending" => {
    if (!game.completed || !userPick) {
      return "pending";
    }

    const homeScore = game.homePoints;
    const awayScore = game.awayPoints;

    if (homeScore === undefined || awayScore === undefined) {
      return "pending";
    }

    // Use winning team from backend data
    const winningTeam = winningTeams[String(game.id)];
    if (winningTeam === undefined) {
      return "pending";
    }

    // Check if it's a push (no winning team means it was a push)
    if (winningTeam === null) {
      return "push";
    }

    // Check if user's pick was correct
    return userPick.pick === winningTeam ? "correct" : "incorrect";
  };

  // Lookup a user's pick for a game
  const getUserPick = (
    gameId: string | number,
    userEmail: string
  ): PickedGame | undefined => {
    const userPicksList = userPicks?.picks[userEmail];
    if (!userPicksList) return undefined;
    const gidNum = Number(gameId);
    return userPicksList.find((pick) => Number(pick.gameId) === gidNum);
  };

  const fetchAvailableYearsAndWeeks = useCallback(async () => {
    if (!leagueId) return;

    try {
      const result = await getAvailableYearsAndWeeks(leagueId);
      if (result instanceof Error) {
        throw result;
      }

      // Handle case where response is an empty map or missing availableScores
      if (
        !result.availableScores ||
        Object.keys(result.availableScores).length === 0
      ) {
        setAvailableYearsAndWeeks({});
        // No scores yet — keep UI usable without showing an error
        setDataAvailable(false);
        return;
      }

      setAvailableYearsAndWeeks(result.availableScores);
      setDataAvailable(true);
      setLoadingHeader(false);

      // Set default selections if not already set
      const years = Object.keys(result.availableScores)
        .map(Number)
        .sort((a, b) => b - a);
      if (selectedYear === 0) {
        setSelectedYear(years[0]);
      }

      const weeks = result.availableScores[years[0]] || [];
      if (selectedWeek === 0 && weeks.length > 0) {
        // Select the highest week number (most recent)
        setSelectedWeek(Math.max(...weeks));
      }
    } catch (err: any) {
      setError(err.message || "Failed to fetch available years and weeks");
      setDataAvailable(false);
    }
  }, [leagueId, getAvailableYearsAndWeeks, selectedYear, selectedWeek]);

  // Initial calendar + scores defaults
  useEffect(() => {
    fetchCalendarCurrent();
  }, [fetchCalendarCurrent]);

  const fetchYearData = useCallback(async () => {
    if (!leagueId || selectedYear === 0 || selectedWeek === 0 || !dataAvailable)
      return;

    try {
      setLoadingScores(true);
      setError(null);

      const result = await getYearScores(leagueId, selectedYear);
      if (result instanceof Error) {
        throw result;
      }

      // Handle case where playerScores is empty or missing
      if (!result.playerScores || result.playerScores.length === 0) {
        setYearScores([]);
        setError("No player scores available for this year.");
        return;
      }

      setYearScores(result.playerScores);
    } catch (err: any) {
      setError(err.message || "Failed to fetch year scores");
    } finally {
      setLoadingScores(false);
    }
  }, [leagueId, getYearScores, selectedYear, selectedWeek, dataAvailable]);

  const fetchWeekData = useCallback(
    async (silent: boolean = false) => {
      if (!leagueId) return;

      // Don't fetch if year/week are not set yet
      if (selectedYear === 0 || selectedWeek === 0) {
        return;
      }

      // Cancel any existing fetch
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this fetch
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Allow restarting fetch if week/year changed
      if (fetchingRef.current) {
        // Reset the fetching flag to allow restart
        fetchingRef.current = false;
      }

      fetchingRef.current = true;

      // Track the current fetch to prevent stale data
      currentFetchRef.current = { year: selectedYear, week: selectedWeek };

      try {
        if (!silent) {
          // Clear old data immediately when starting new fetch
          setWeekScores([]);
          setWeekComplete(false);
          setWinningTeams({});
          setSelectedGames([]);
          setUserPicks(null);
          setLoadingScores(true);
          setLoadingGames(true);
        }
        setError(null);

        const gamesData = await getSelectedGames(
          leagueId,
          selectedYear,
          selectedWeek
        );
        if (gamesData instanceof Error) {
          throw new Error(
            `Failed to fetch selected games: ${gamesData.message}`
          );
        }

        // Check if fetch was cancelled
        if (abortController.signal.aborted) {
          return;
        }

        // Validate this is still the current fetch
        if (
          !currentFetchRef.current ||
          currentFetchRef.current.year !== selectedYear ||
          currentFetchRef.current.week !== selectedWeek
        ) {
          return;
        }

        // Handle case where games is empty or missing
        if (!gamesData.games || gamesData.games.length === 0) {
          setSelectedGames([]);
          setGameOfTheWeek(gamesData.gameOfTheWeek || null);
          setWeekReady(Boolean(gamesData.ready));
          setError("No games selected for this week.");
          return;
        }

        setSelectedGames(gamesData.games);
        setGameOfTheWeek(gamesData.gameOfTheWeek || null);
        setWeekReady(Boolean(gamesData.ready));
        // Don't set loadingGames to false yet - wait until all data is fetched

        // Fetch week scores
        if (selectedYear > 0 && selectedWeek > 0) {
          const scoresResult = await getWeekScores(
            leagueId,
            selectedYear,
            selectedWeek
          );
          if (scoresResult instanceof Error) {
            throw scoresResult;
          }

          // Check if fetch was cancelled
          if (abortController.signal.aborted) {
            return;
          }

          // Validate this is still the current fetch
          if (
            !currentFetchRef.current ||
            currentFetchRef.current.year !== selectedYear ||
            currentFetchRef.current.week !== selectedWeek
          ) {
            return;
          }

          // Handle case where playerScores is empty or missing
          if (
            !scoresResult.playerScores ||
            scoresResult.playerScores.length === 0
          ) {
            setWeekScores([]);
            setWeekComplete(scoresResult.complete || false);
          } else {
            setWeekScores(scoresResult.playerScores);
            setWeekComplete(scoresResult.complete);
            setWinningTeams(scoresResult.winningTeams || {});
          }
          setLastUpdated(new Date());
          setLoadingScores(false);
        } else {
          // No selected year/week — skip scores fetch
          setWeekScores([]);
          setWeekComplete(false);
          setWinningTeams({});
          setLoadingScores(false);
        }

        // Fetch user picks for all players
        const picksData = await getAllPicksForWeek(
          leagueId,
          selectedYear,
          selectedWeek
        );
        if (picksData instanceof Error) {
          throw new Error(
            `Failed to get all visible user picks: ${picksData.message}`
          );
        }

        // Check if fetch was cancelled
        if (abortController.signal.aborted) {
          return;
        }

        // Validate this is still the current fetch
        if (
          !currentFetchRef.current ||
          currentFetchRef.current.year !== selectedYear ||
          currentFetchRef.current.week !== selectedWeek
        ) {
          return;
        }

        setUserPicks(picksData);

        // Preload avatars and users for visible pickers
        const emails = Object.keys(picksData.picks);
        if (emails.length > 0) {
          preloadUsers(emails);
          preloadAvatars(emails);
        }

        // Fetch polls and records data if year and week are available
        if (selectedYear > 0 && selectedWeek > 0) {
          const allTeamIds = Array.from(
            new Set([
              ...gamesData.games.map((g: SelectedGame) => g.homeTeam.id),
              ...gamesData.games.map((g: SelectedGame) => g.awayTeam.id),
            ])
          );
          if (allTeamIds.length > 0) {
            const pollsAndRecordsResponse = await getPollsAndRecords(
              selectedYear,
              selectedWeek,
              allTeamIds
            );
            if (pollsAndRecordsResponse instanceof Error) {
              setError(
                `Error fetching polls: ${pollsAndRecordsResponse.message}`
              );
              setPollsData(null);
              setRecordsData(null);
              return;
            } else {
              setPollsData(pollsAndRecordsResponse.polls);
              setRecordsData(pollsAndRecordsResponse.records);
            }
          }
        }
      } catch (err: any) {
        if (!silent) setError(err.message || "Failed to fetch week data");
      } finally {
        fetchingRef.current = false;
        abortControllerRef.current = null;
        if (!silent) {
          // Only set loading to false if we have data or if there was an error
          // This prevents showing empty data during transitions
          setLoadingScores(false);
          setLoadingGames(false);
        }
      }
    },
    [
      leagueId,
      selectedYear,
      selectedWeek,
      preloadUsers,
      preloadAvatars,
      getPollsAndRecords,
      getSelectedGames,
      getAllPicksForWeek,
      getWeekScores,
    ]
  );

  useEffect(() => {
    if (leagueId) {
      setDataAvailable(false);
      setError(null);
      fetchAvailableYearsAndWeeks();
    }
  }, [leagueId, fetchAvailableYearsAndWeeks]);

  useEffect(() => {
    if (viewMode === "year") {
      fetchYearData();
    } else {
      fetchWeekData();
    }
    // fetchWeekData intentionally omitted from deps to prevent infinite loops
    // since fetchWeekData changes on every render due to its dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    viewMode,
    leagueId,
    selectedYear,
    selectedWeek,
    dataAvailable,
    fetchYearData,
  ]);

  // Interval polling while any selected game is live
  useEffect(() => {
    if (!leagueId || viewMode !== "week") return;
    const anyLive = selectedGames.some(
      (g) => isGameStarted(g.game.time) && !g.game.completed
    );
    if (!anyLive) return;
    const id = setInterval(() => {
      fetchWeekData(true);
    }, 20000);
    return () => clearInterval(id);
  }, [leagueId, viewMode, selectedGames, fetchWeekData]);

  // Cleanup effect to cancel any pending fetches on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newYear = Number(event.target.value);
    setSelectedYear(newYear);

    // Reset week to first available week for the new year
    // Prefer calendar weeks; fallback to available scores weeks
    const calWeeks = calendarWeeksByYear[newYear];
    if (!calWeeks) {
      fetchCalendarForYear(newYear).then((w) => {
        const useWeeks =
          w && w.length > 0 ? w : availableYearsAndWeeks[newYear] || [];
        if (useWeeks.length > 0) {
          // If switching to current calendar year, prefer current week; else first
          const cur = currentCalendarRef.current;
          if (
            cur?.year === newYear &&
            typeof cur?.week === "number" &&
            cur.week > 0 &&
            useWeeks.includes(cur.week)
          ) {
            setSelectedWeek(cur.week);
          } else {
            setSelectedWeek(useWeeks[0]);
          }
        }
      });
    } else if (calWeeks.length > 0) {
      const cur = currentCalendarRef.current;
      if (
        cur?.year === newYear &&
        typeof cur?.week === "number" &&
        cur.week > 0 &&
        calWeeks.includes(cur.week)
      ) {
        setSelectedWeek(cur.week);
      } else {
        setSelectedWeek(calWeeks[0]);
      }
    } else {
      const weeks = availableYearsAndWeeks[newYear] || [];
      if (weeks.length > 0) setSelectedWeek(weeks[0]);
    }
  };

  const handleWeekChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newWeek = Number(event.target.value);
    setSelectedWeek(newWeek);
  };

  const handleViewChange = (
    event: React.MouseEvent<HTMLElement>,
    newView: ViewMode | null
  ) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  if (!leagueId) {
    return (
      <Box sx={{ padding: 2 }}>
        <Alert severity="warning">
          No league selected. Please select a league to view the scoreboard.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Stack spacing={2}>
        {/* Header */}
        <ScoreboardHeader
          viewMode={viewMode}
          selectedYear={selectedYear}
          selectedWeek={selectedWeek}
          availableYearsAndWeeks={{ availableScores: availableYearsAndWeeks }}
          lastUpdated={lastUpdated}
          isMobile={isMobile}
          loading={loadingHeader}
          onViewChange={handleViewChange}
          onYearChange={handleYearChange}
          onWeekChange={handleWeekChange}
          onRefresh={() => fetchWeekData(true)}
        />

        {/* Error */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Content */}
        {!error && (
          <>
            {viewMode === "year" ? (
              /* Year View */
              <ScoreboardTable
                scores={yearScores}
                selectedYear={selectedYear}
                rules={rules}
                getDisplayName={getDisplayName}
                isMobile={isMobile}
                loading={loadingScores}
                userEmail={userEmail}
                viewMode="year"
              />
            ) : (
              /* Week View */
              <Stack spacing={2}>
                {/* Week standings */}
                <ScoreboardTable
                  scores={weekScores}
                  weekComplete={weekComplete}
                  selectedYear={selectedYear}
                  selectedWeek={selectedWeek}
                  rules={rules}
                  selectedGames={selectedGames}
                  gameOfTheWeek={gameOfTheWeek}
                  getDisplayName={getDisplayName}
                  isMobile={isMobile}
                  loading={loadingScores}
                  userEmail={userEmail}
                  viewMode="week"
                />

                {/* Game results */}
                <GameResults
                  selectedGames={selectedGames}
                  gameOfTheWeek={gameOfTheWeek}
                  userPicks={userPicks}
                  pollsData={pollsData}
                  recordsData={recordsData}
                  isMobile={isMobile}
                  weekReady={weekReady}
                  selectedYear={selectedYear}
                  selectedWeek={selectedWeek}
                  userEmail={userEmail}
                  rules={rules}
                  winningTeams={winningTeams}
                  loading={loadingGames}
                  getUserPick={getUserPick}
                  getGameResult={getGameResult}
                />
              </Stack>
            )}
          </>
        )}
      </Stack>
    </Box>
  );
};

export default Scoreboard;
